import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FormFieldWrapperComponent } from '@shared-lib/components/form-components/form-field-wrapper/form-field-wrapper.component';
import { InputNumberComponent } from '@shared-lib/components/form-components/input-number/input-number.component';
import { InputTermComponent } from '@shared-lib/components/form-components/input-term/input-term.component';
import { ValidationMessageComponent } from '@shared-lib/components/form-components/validation-message/validation-message.component';
import { WarningComponent } from '@shared-lib/components/warning/warning.component';
import {
  MAX_AGE_ELIGIBLE_FOR_MORTGAGE_IN_MONTHS,
  MAX_MORTGAGE_DURATION_IN_MONTHS,
  MIN_MORTGAGE_DURATION_IN_MONTHS,
} from '@shared-lib/constants/mortgage';
import { SmartSearchNewPropertyBasicDetailsFormModel } from '@shared-lib/models/sign-up/sign-up-smart-search/smart-search-dialog-basic-details-form.model';
import {
  ScreenSizeBreakpointNames,
  ScreenSizeBreakpointObserverService,
} from '@shared-lib/services/screen-size-breakpoint-observer.service';
import { ButtonModule } from 'primeng/button';
import { pairwise } from 'rxjs';

@Component({
  selector: 'cui-smart-search-new-property-basic-details-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    FormFieldWrapperComponent,
    InputNumberComponent,
    InputTermComponent,
    ValidationMessageComponent,
    WarningComponent,
  ],
  templateUrl: './smart-search-new-property-basic-details-form.component.html',
  styleUrls: ['./smart-search-new-property-basic-details-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SmartSearchNewPropertyBasicDetailsFormComponent implements OnInit, AfterViewInit {
  @Input({ required: true }) formGroup!: FormGroup<SmartSearchNewPropertyBasicDetailsFormModel>;
  @Input({ required: true }) ageInYears!: number;
  @Input() isBackButtonVisible = true;
  @Input() propertyPriceLabel = 'Maximum property price';

  @Output() nextClicked = new EventEmitter();
  @Output() backClicked = new EventEmitter();

  private readonly destroyRef = inject(DestroyRef);
  private readonly screenSizeBreakpointObserverService = inject(ScreenSizeBreakpointObserverService);

  protected readonly size$ = this.screenSizeBreakpointObserverService.screenSizeBreakpointName$;

  protected readonly ScreenSizeBreakpointNames = ScreenSizeBreakpointNames;
  protected readonly MAX_MORTGAGE_DURATION_IN_MONTHS = MAX_MORTGAGE_DURATION_IN_MONTHS;

  get isMaxTermLimited(): boolean {
    return MAX_AGE_ELIGIBLE_FOR_MORTGAGE_IN_MONTHS / 12 - this.formGroup.value.ageInYears! < MAX_MORTGAGE_DURATION_IN_MONTHS / 12;
  }

  ngOnInit(): void {
    this.formGroup.valueChanges.pipe(takeUntilDestroyed(this.destroyRef), pairwise()).subscribe(([previousValue, currentValue]) => {
      if (previousValue.ageInYears !== currentValue.ageInYears && this.formGroup.controls.ageInYears.valid) {
        const maxTermBasedOnAge = MAX_AGE_ELIGIBLE_FOR_MORTGAGE_IN_MONTHS / 12 - currentValue.ageInYears!;
        const newTermInMonths = Math.max(
          MIN_MORTGAGE_DURATION_IN_MONTHS, // Minimum is 5 years
          maxTermBasedOnAge > MAX_MORTGAGE_DURATION_IN_MONTHS / 12 ? MAX_MORTGAGE_DURATION_IN_MONTHS : maxTermBasedOnAge * 12,
        );
        this.formGroup.patchValue({
          newTermInMonths,
        });
      }
    });
  }

  ngAfterViewInit(): void {
    if (!this.formGroup.value.ageInYears) {
      const maxTermBasedOnAge = MAX_AGE_ELIGIBLE_FOR_MORTGAGE_IN_MONTHS / 12 - this.ageInYears;
      const newTermInMonths =
        maxTermBasedOnAge > MAX_MORTGAGE_DURATION_IN_MONTHS / 12 ? MAX_MORTGAGE_DURATION_IN_MONTHS : maxTermBasedOnAge * 12;
      this.formGroup.patchValue({
        ageInYears: this.ageInYears,
        newTermInMonths,
      });
    }
  }
}
